import React, { useState } from 'react';
import { Search, Plus, Filter, Edit, Trash2, Star, Eye, ChevronLeft, ChevronRight, Download, Package, TrendingUp, AlertCircle, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAdminProducts, deleteAdminProduct } from '../../redux/adminProductsSlice';

const Products = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const itemsPerPage = 5;

  const dispatch = useDispatch();
  const { products, loading, error } = useSelector((state) => state.adminProducts);

  React.useEffect(() => {
    dispatch(fetchAdminProducts());
  }, [dispatch]);

  const categories = ['All Categories', 'Indoor Plants', 'Air Purifying', 'Medicinal', 'Succulents', 'Ferns'];
  const statuses = ['All Status', 'Active', 'Low Stock', 'Out of Stock'];

  const getStatusConfig = (status) => {
    switch (status) {
      case 'Active': return { icon: CheckCircle, color: '#10b981', bg: '#d1fae5' };
      case 'Low Stock': return { icon: AlertCircle, color: '#f59e0b', bg: '#fed7aa' };
      case 'Out of Stock': return { icon: AlertCircle, color: '#ef4444', bg: '#fee2e2' };
      default: return { icon: Package, color: '#64748b', bg: '#f1f5f9' };
    }
  };

  const filteredProducts = (products || []).filter(product => {
    const sellerName = product.sellerId?.storeName || product.sellerId?.username || '';
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          sellerName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || filterCategory === 'All Categories' || product.category === filterCategory;
    
    // calculate actual status
    let status = 'Active';
    if (product.stock === 0) status = 'Out of Stock';
    else if (product.stock < 15) status = 'Low Stock';
    
    const matchesStatus = filterStatus === 'all' || filterStatus === 'All Status' || status === filterStatus;
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const paginatedProducts = filteredProducts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const stats = {
    total: products.length,
    active: products.filter(p => p.countInStock >= 15).length,
    lowStock: products.filter(p => p.countInStock > 0 && p.countInStock < 15).length,
    outOfStock: products.filter(p => p.countInStock === 0).length,
    totalValue: products.reduce((sum, p) => sum + (p.price * p.countInStock), 0)
  };

  const handleDelete = (id) => {
    if (window.confirm("Delete this product?")) {
      dispatch(deleteAdminProduct(id));
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedProducts(paginatedProducts.map(p => p.id));
    } else {
      setSelectedProducts([]);
    }
  };

  const handleSelectProduct = (id) => {
    if (selectedProducts.includes(id)) {
      setSelectedProducts(selectedProducts.filter(pid => pid !== id));
    } else {
      setSelectedProducts([...selectedProducts, id]);
    }
  };

  if (loading) return <div className="p-4">Loading products...</div>;
  if (error) return <div className="p-4 text-danger">{error}</div>;

  return (
    <div className="admin-products-modern">
      {/* Header */}
      <div className="products-header">
        <div>
          <h1 className="page-title">Products Catalog</h1>
          <p className="page-subtitle">Manage products from all sellers across the platform</p>
        </div>
        <div className="header-actions">
          <button className="btn-export">
            <Download size={16} />
            Export
          </button>
          <Link to="/admin/products/add" className="btn-primary">
            <Plus size={16} />
            Add New Product
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon green">
            <Package size={20} />
          </div>
          <div>
            <h3>{stats.total}</h3>
            <span>Total Products</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon blue">
            <CheckCircle size={20} />
          </div>
          <div>
            <h3>{stats.active}</h3>
            <span>Active</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon orange">
            <AlertCircle size={20} />
          </div>
          <div>
            <h3>{stats.lowStock}</h3>
            <span>Low Stock</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon purple">
            <TrendingUp size={20} />
          </div>
          <div>
            <h3>₹{stats.totalValue.toLocaleString()}</h3>
            <span>Inventory Value</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <Search size={18} />
          <input 
            type="text" 
            placeholder="Search by product name or seller..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-selects">
          <select className="filter-select" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
            {categories.map(cat => (
              <option key={cat} value={cat === 'All Categories' ? 'all' : cat}>{cat}</option>
            ))}
          </select>
          <select className="filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            {statuses.map(status => (
              <option key={status} value={status === 'All Status' ? 'all' : status}>{status}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Products Table */}
      <div className="table-container">
        <div className="table-responsive">
          <table className="products-table">
            <thead>
              <tr>
                <th className="checkbox-col">
                  <input 
                    type="checkbox" 
                    className="checkbox-custom"
                    checked={selectedProducts.length === paginatedProducts.length && paginatedProducts.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
                <th>Product Info</th>
                <th>Category</th>
                <th>Seller</th>
                <th>Price</th>
                <th>Stock</th>
                <th>Sales</th>
                <th>Status</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedProducts.map((product) => {
                let pStatus = 'Active';
                if (product.stock === 0) pStatus = 'Out of Stock';
                else if (product.stock < 15) pStatus = 'Low Stock';
                
                const statusConfig = getStatusConfig(pStatus);
                const StatusIcon = statusConfig.icon;
                
                return (
                  <tr key={product._id}>
                    <td className="checkbox-col">
                      <input 
                        type="checkbox" 
                        className="checkbox-custom"
                        checked={selectedProducts.includes(product._id)}
                        onChange={() => handleSelectProduct(product._id)}
                      />
                    </td>
                    <td>
                      <div className="product-cell">
                        <div className="product-image">
                          {product.image ? (
                            <img src={product.image} alt={product.name} style={{width:'100%', height:'100%', objectFit:'cover', borderRadius:'8px'}} />
                          ) : (
                            <span>🪴</span>
                          )}
                        </div>
                        <div className="product-info">
                          <div className="product-name">{product.name}</div>
                          <div className="product-rating">
                            <Star size={12} fill="#f59e0b" color="#f59e0b" />
                            <span>{product.rating || 0}</span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>{product.category}</td>
                    <td>{product.sellerId?.storeName || product.sellerId?.username}</td>
                    <td className="price-cell">₹{product.price}</td>
                    <td className={`stock-cell ${product.stock === 0 ? 'out' : product.stock < 15 ? 'low' : ''}`}>
                      {product.stock} pcs
                    </td>
                    <td className="sales-cell">{product.reviewsCount || 0} reviews</td>
                    <td>
                      <span className="status-badge" style={{ backgroundColor: statusConfig.bg, color: statusConfig.color }}>
                        <StatusIcon size={12} />
                        {pStatus}
                      </span>
                    </td>
                    <td className="text-right">
                      <div className="action-buttons">
                        <button className="action-btn view" title="View">
                          <Eye size={16} />
                        </button>
                        <button className="action-btn delete" onClick={() => handleDelete(product._id)} title="Delete">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {paginatedProducts.length === 0 && (
                <tr>
                  <td colSpan="9" className="text-center py-5 text-muted">No products found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Bulk Actions */}
        {selectedProducts.length > 0 && (
          <div className="bulk-actions">
            <span>{selectedProducts.length} products selected</span>
            <div className="bulk-buttons">
              <button className="bulk-btn">Bulk Delete</button>
              <button className="bulk-btn">Bulk Status</button>
            </div>
          </div>
        )}

        {/* Pagination */}
        <div className="pagination-wrapper">
          <div className="pagination-info">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredProducts.length)} of {filteredProducts.length} products
          </div>
          <div className="pagination-buttons">
            <button 
              className={`page-btn ${currentPage === 1 ? 'disabled' : ''}`} 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft size={16} /> Previous
            </button>
            {[...Array(Math.min(totalPages, 5))].map((_, i) => {
              let pageNum = i + 1;
              if (totalPages > 5 && currentPage > 3) {
                pageNum = currentPage - 3 + i;
                if (pageNum > totalPages) return null;
              }
              return (
                <button 
                  key={i}
                  className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
            {totalPages > 5 && currentPage < totalPages - 2 && <span className="page-dots">...</span>}
            {totalPages > 5 && currentPage < totalPages - 2 && (
              <button className="page-number" onClick={() => setCurrentPage(totalPages)}>
                {totalPages}
              </button>
            )}
            <button 
              className={`page-btn ${currentPage === totalPages ? 'disabled' : ''}`} 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      
    </div>
  );
};

export default Products;